import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Sparkles, Save, Eye, Send, Trash2, Plus, Image as ImageIcon,
  Smartphone, Monitor, Mail, CheckCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import AdminPageHeader from '../../components/AdminPageHeader';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';

const ACCENT_PRESETS = [
  { color: '#C4A084', name: 'Goldbraun (Standard)' },
  { color: '#A87E5E', name: 'Sandbraun' },
  { color: '#8B7355', name: 'Erdbraun' },
  { color: '#6D5A47', name: 'Dunkelbraun' },
  { color: '#D4B595', name: 'Hellbeige' },
  { color: '#E8D5B7', name: 'Champagner' },
  { color: '#B85C38', name: 'Terrakotta' },
  { color: '#D4756B', name: 'Korall-Rosa' },
  { color: '#E8A87C', name: 'Apricot' },
  { color: '#85B79D', name: 'Salbeigrün' },
  { color: '#6B8E7F', name: 'Tannengrün' },
  { color: '#8DA3B5', name: 'Taubenblau' },
  { color: '#7B8FA1', name: 'Steingrau' },
  { color: '#C8A2C8', name: 'Mauve' },
  { color: '#D9A5B3', name: 'Altrosa' },
  { color: '#B5651D', name: 'Karamell' },
];

// Newsletter-Vorlagen für schnellen Start
const NEWSLETTER_TEMPLATES = [
  {
    id: 'welcome',
    name: '🌟 Willkommen',
    description: 'Begrüßung für Neuabonnenten',
    accent_color: '#C4A084',
    subject: 'Herzlich willkommen bei Homeyoursoul!',
    preheader: 'Schön, dass Sie dabei sind',
    hero_title: 'Willkommen in unserer kleinen Welt',
    hero_text: 'Wir freuen uns, dass Sie Teil unserer Homeyoursoul-Familie geworden sind. Entdecken Sie handgemachte Schätze, die Ihrem Zuhause Seele verleihen.',
    intro_text: 'Als kleines Dankeschön finden Sie hier eine Auswahl unserer beliebtesten Produkte.',
    cta_label: 'Jetzt entdecken',
  },
  {
    id: 'spring',
    name: '🌸 Frühling',
    description: 'Frühlings-Highlights',
    accent_color: '#85B79D',
    subject: 'Frische Frühlings-Highlights bei Homeyoursoul',
    preheader: 'Lassen Sie den Frühling einziehen',
    hero_title: 'Frühling im Herzen, Frühling zu Hause',
    hero_text: 'Die Tage werden länger, die Natur erwacht. Holen Sie sich frische Farben und Düfte ins Zuhause.',
    intro_text: '',
    cta_label: 'Frühlings-Kollektion ansehen',
  },
  {
    id: 'summer',
    name: '☀️ Sommer',
    description: 'Sommerliche Akzente',
    accent_color: '#E8A87C',
    subject: 'Sommerliche Akzente für Ihr Zuhause',
    preheader: 'Leichtigkeit & Sonnenstrahlen',
    hero_title: 'Sommerzauber für Ihre vier Wände',
    hero_text: 'Helle Farben, frische Düfte, leichte Materialien – der Sommer bringt Freude in jede Ecke.',
    intro_text: '',
    cta_label: 'Sommer-Highlights',
  },
  {
    id: 'autumn',
    name: '🍂 Herbst',
    description: 'Gemütliche Herbst-Stimmung',
    accent_color: '#B85C38',
    subject: 'Goldene Herbstmomente bei Homeyoursoul',
    preheader: 'Wärme & Gemütlichkeit',
    hero_title: 'Herbst-Gemütlichkeit für Ihr Zuhause',
    hero_text: 'Kerzen, warme Farben, weiche Stoffe – machen Sie es sich gemütlich.',
    intro_text: '',
    cta_label: 'Herbst-Auswahl',
  },
  {
    id: 'christmas',
    name: '🎄 Weihnachten',
    description: 'Weihnachtliche Geschenkideen',
    accent_color: '#6D5A47',
    subject: 'Weihnachtliche Geschenkideen mit Liebe',
    preheader: 'Handgemacht, persönlich, einzigartig',
    hero_title: 'Geschenke, die von Herzen kommen',
    hero_text: 'In dieser besonderen Zeit sind handgemachte Geschenke ein Zeichen von Wertschätzung. Finden Sie das passende Präsent für Ihre Liebsten.',
    intro_text: '',
    cta_label: 'Weihnachts-Kollektion',
  },
  {
    id: 'sale',
    name: '🏷️ Aktion',
    description: 'Rabatt-Aktion',
    accent_color: '#D4756B',
    subject: '✨ Exklusive Aktion nur für Sie',
    preheader: '10% Rabatt auf Ihren nächsten Einkauf',
    hero_title: 'Ein kleines Dankeschön',
    hero_text: 'Sparen Sie 10% auf Ihre nächste Bestellung mit dem Code: DANKE10',
    intro_text: 'Diese Aktion ist nur für unsere treuen Abonnenten und gilt für die nächsten 14 Tage.',
    cta_label: 'Jetzt einlösen',
  },
  {
    id: 'new-arrivals',
    name: '✨ Neuheiten',
    description: 'Neue Produkte im Shop',
    accent_color: '#D9A5B3',
    subject: 'Frisch eingetroffen: Unsere neuen Schätze',
    preheader: 'Entdecken Sie die Neuheiten',
    hero_title: 'Neue Schätze warten auf Sie',
    hero_text: 'Mit Liebe ausgesucht und handgefertigt – unsere neuesten Produkte sind eingetroffen.',
    intro_text: '',
    cta_label: 'Neuheiten ansehen',
  },
  {
    id: 'minimal',
    name: '📝 Minimalistisch',
    description: 'Schlichte Nachricht',
    accent_color: '#8B7355',
    subject: 'Eine Nachricht von Homeyoursoul',
    preheader: '',
    hero_title: '',
    hero_text: '',
    intro_text: '',
    cta_label: 'Mehr erfahren',
  },
];

const emptyDraft = () => ({
  id: null,
  title: 'Neuer Newsletter',
  subject: '',
  preheader: '',
  hero_title: '',
  hero_text: '',
  hero_image: '',
  cta_label: 'Jetzt entdecken',
  cta_link: 'https://homeyoursoul.com/products',
  intro_text: '',
  products: [],
  footer_note: '',
  accent_color: '#C4A084',
});

export default function NewsletterDesigner() {
  const [draft, setDraft] = useState(emptyDraft());
  const [drafts, setDrafts] = useState([]);
  const [previewMode, setPreviewMode] = useState('desktop');
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState('kontakt@homeyoursoul.shop');
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);

  useEffect(() => { fetchDrafts(); }, []);

  const fetchDrafts = async () => {
    try {
      const res = await axiosInstance.get('/newsletter-designer/drafts');
      setDrafts(res.data || []);
    } catch (e) { /* ignore */ }
  };

  const update = (key, value) => setDraft({ ...draft, [key]: value });

  const addProduct = () => {
    setDraft({
      ...draft,
      products: [...draft.products, { title: '', description: '', image_url: '', link: '', price: '' }],
    });
  };

  const updateProduct = (idx, key, value) => {
    const products = [...draft.products];
    products[idx] = { ...products[idx], [key]: value };
    setDraft({ ...draft, products });
  };

  const removeProduct = (idx) => {
    setDraft({ ...draft, products: draft.products.filter((_, i) => i !== idx) });
  };

  const handleAiGenerate = async () => {
    if (!aiPrompt.trim()) return toast.error('Bitte geben Sie eine Idee ein');
    setAiLoading(true);
    try {
      const res = await axiosInstance.post('/newsletter-designer/ai-generate', {
        prompt: aiPrompt,
        include_products: true,
      });
      const ai = res.data || {};
      setDraft({
        ...draft,
        subject: ai.subject || draft.subject,
        preheader: ai.preheader || draft.preheader,
        hero_title: ai.hero_title || draft.hero_title,
        hero_text: ai.hero_text || draft.hero_text,
        intro_text: ai.intro_text || draft.intro_text,
        cta_label: ai.cta_label || draft.cta_label,
        products: Array.isArray(ai.products) && ai.products.length
          ? ai.products.map((p) => ({
              title: p.title || '',
              description: p.description || '',
              price: p.price || '',
              image_url: '',
              link: '',
            }))
          : draft.products,
      });
      toast.success('KI-Entwurf eingefügt - bitte anpassen & speichern');
    } catch (e) {
      toast.error('KI-Generierung fehlgeschlagen: ' + (e.response?.data?.detail || e.message));
    } finally {
      setAiLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (draft.id) {
        await axiosInstance.put(`/newsletter-designer/drafts/${draft.id}`, draft);
        toast.success('Entwurf aktualisiert');
      } else {
        const res = await axiosInstance.post('/newsletter-designer/drafts', draft);
        setDraft({ ...draft, id: res.data.id });
        toast.success('Entwurf gespeichert');
      }
      fetchDrafts();
    } catch (e) {
      toast.error('Fehler beim Speichern');
    } finally { setSaving(false); }
  };

  const handleTestSend = async () => {
    if (!testEmail) return toast.error('Test-E-Mail-Adresse fehlt');
    try {
      await axiosInstance.post('/newsletter-designer/test-send', { to_email: testEmail, draft });
      toast.success(`Test-Newsletter an ${testEmail} gesendet`);
    } catch (e) {
      toast.error('Test-Versand fehlgeschlagen');
    }
  };

  const handleRequestApproval = async () => {
    if (!draft.id) {
      toast.error('Bitte erst speichern');
      return;
    }
    try {
      await axiosInstance.post(`/newsletter-designer/request-approval/${draft.id}`);
      toast.success('Freigabe-Anfrage erstellt - Newsletter wartet auf Versand-Bestätigung');
      setShowApprovalDialog(false);
      fetchDrafts();
    } catch (e) {
      toast.error('Fehler bei Freigabe-Anfrage');
    }
  };

  const handleApproveAndSend = async () => {
    if (!draft.id) return toast.error('Bitte erst speichern');
    if (!window.confirm(`Newsletter "${draft.subject}" jetzt an alle bestätigten Abonnenten versenden?`)) return;
    try {
      const res = await axiosInstance.post(`/newsletter-designer/approve-and-send/${draft.id}`);
      toast.success(`Newsletter versendet: ${res.data.sent} erfolgreich, ${res.data.failed} fehlgeschlagen`);
      fetchDrafts();
    } catch (e) {
      toast.error('Versand fehlgeschlagen: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleSnooze = async () => {
    if (!draft.id) return toast.error('Bitte erst speichern');
    try {
      await axiosInstance.post(`/newsletter-designer/snooze/${draft.id}`);
      toast.success('Newsletter wartet - Sie können später entscheiden');
      fetchDrafts();
    } catch (e) {
      toast.error('Fehler beim Pausieren');
    }
  };

  const handleReject = async () => {
    if (!draft.id) return;
    if (!window.confirm('Diesen Newsletter wirklich verwerfen?')) return;
    try {
      await axiosInstance.post(`/newsletter-designer/reject/${draft.id}`);
      toast.success('Newsletter abgelehnt');
      fetchDrafts();
    } catch (e) {
      toast.error('Fehler beim Ablehnen');
    }
  };

  const loadDraft = (d) => {
    setDraft({ ...emptyDraft(), ...d });
    toast.success('Entwurf geladen');
  };

  const applyTemplate = (tpl) => {
    setDraft({
      ...draft,
      title: tpl.name,
      subject: tpl.subject,
      preheader: tpl.preheader,
      hero_title: tpl.hero_title,
      hero_text: tpl.hero_text,
      intro_text: tpl.intro_text,
      cta_label: tpl.cta_label,
      accent_color: tpl.accent_color,
    });
    toast.success(`Vorlage "${tpl.name}" geladen`);
  };

  const previewHtml = useMemo(() => buildPreviewHtml(draft), [draft]);

  return (
    <div className="min-h-screen py-4 px-3 sm:px-6 lg:px-8" style={{ backgroundColor: '#F5EDE4' }} data-testid="newsletter-designer">
      <div className="max-w-[1500px] mx-auto">
        <AdminPageHeader
          title="Newsletter Designer"
          subtitle="Newsletter visuell gestalten mit KI-Unterstützung & Live-Vorschau"
          icon={Mail}
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* LEFT: EDITOR */}
          <div className="space-y-4">
            {/* AI Helper */}
            <Card className="rounded-none border-[#C4A084]">
              <CardHeader className="bg-gradient-to-r from-[#C4A084] to-[#B89968] text-white py-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Sparkles className="h-4 w-4" /> KI-Entwurf erstellen
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 pt-4">
                <Textarea
                  data-testid="ai-prompt-textarea"
                  placeholder="z.B. 'Frühlings-Newsletter mit Fokus auf Vasen und 10% Rabattcode FRUEHLING10'"
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  rows={3}
                  className="text-sm"
                />
                <Button
                  data-testid="ai-generate-btn"
                  onClick={handleAiGenerate}
                  disabled={aiLoading}
                  className="w-full bg-gradient-to-r from-[#C4A084] to-[#B89968] text-white rounded-none"
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  {aiLoading ? 'KI denkt nach...' : 'Mit KI generieren'}
                </Button>
              </CardContent>
            </Card>

            {/* Templates */}
            <Card className="rounded-none border-[#C4A084]">
              <CardHeader className="py-3">
                <CardTitle className="text-base">Vorlagen ({NEWSLETTER_TEMPLATES.length})</CardTitle>
                <p className="text-xs text-gray-500 mt-1">Klicken zum Anwenden - alle Felder werden gefüllt</p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-2">
                  {NEWSLETTER_TEMPLATES.map((tpl) => (
                    <button
                      key={tpl.id}
                      onClick={() => applyTemplate(tpl)}
                      data-testid={`template-${tpl.id}`}
                      className="text-left p-3 border border-[#E8E0D5] hover:border-[#C4A084] hover:bg-[#FAF7F2] transition-all group"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-3 h-3 flex-shrink-0" style={{ backgroundColor: tpl.accent_color }} />
                        <span className="text-sm font-medium">{tpl.name}</span>
                      </div>
                      <p className="text-xs text-gray-500 group-hover:text-[#8B7355]">{tpl.description}</p>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Basics */}
            <Card className="rounded-none border-[#C4A084]">
              <CardHeader className="py-3"><CardTitle className="text-base">Newsletter-Basis</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-xs">Interner Titel</Label>
                  <Input data-testid="draft-title-input" value={draft.title} onChange={(e) => update('title', e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs">Betreff (in E-Mail-Postfach sichtbar)</Label>
                  <Input data-testid="subject-input" value={draft.subject} onChange={(e) => update('subject', e.target.value)} placeholder="z.B. Frische Frühlings-Highlights bei Homeyoursoul" />
                </div>
                <div>
                  <Label className="text-xs">Vorschautext (Preheader)</Label>
                  <Input value={draft.preheader} onChange={(e) => update('preheader', e.target.value)} placeholder="Kurzer Teasertext..." />
                </div>
                <div>
                  <Label className="text-xs">Akzentfarbe</Label>
                  <div className="grid grid-cols-8 gap-1.5 mt-2">
                    {ACCENT_PRESETS.map((preset) => (
                      <button
                        key={preset.color}
                        data-testid={`accent-color-${preset.color}`}
                        onClick={() => update('accent_color', preset.color)}
                        className={`relative w-full aspect-square border-2 transition-all hover:scale-110 ${draft.accent_color === preset.color ? 'border-black ring-2 ring-offset-1 ring-black' : 'border-white'}`}
                        style={{ backgroundColor: preset.color }}
                        title={preset.name}
                        aria-label={`Farbe ${preset.name}`}
                      />
                    ))}
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <Label className="text-xs">Eigene Farbe:</Label>
                    <input
                      type="color"
                      value={draft.accent_color}
                      onChange={(e) => update('accent_color', e.target.value)}
                      className="w-10 h-8 border border-gray-300 cursor-pointer"
                      data-testid="custom-color-picker"
                    />
                    <Input
                      value={draft.accent_color}
                      onChange={(e) => update('accent_color', e.target.value)}
                      className="font-mono text-xs h-8 w-28"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Hero */}
            <Card className="rounded-none border-[#C4A084]">
              <CardHeader className="py-3"><CardTitle className="text-base">Hauptbotschaft</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-xs">Überschrift</Label>
                  <Input data-testid="hero-title-input" value={draft.hero_title} onChange={(e) => update('hero_title', e.target.value)} placeholder="Willkommen im Frühling" />
                </div>
                <div>
                  <Label className="text-xs">Begrüßungstext</Label>
                  <Textarea value={draft.hero_text} onChange={(e) => update('hero_text', e.target.value)} rows={3} placeholder="Liebe Kundinnen und Kunden..." />
                </div>
                <div>
                  <Label className="text-xs">Hero-Bild URL (optional)</Label>
                  <Input value={draft.hero_image} onChange={(e) => update('hero_image', e.target.value)} placeholder="https://..." />
                </div>
                <div>
                  <Label className="text-xs">Einleitungstext</Label>
                  <Textarea value={draft.intro_text} onChange={(e) => update('intro_text', e.target.value)} rows={2} />
                </div>
              </CardContent>
            </Card>

            {/* Products */}
            <Card className="rounded-none border-[#C4A084]">
              <CardHeader className="py-3 flex flex-row items-center justify-between">
                <CardTitle className="text-base">Produkte vorstellen ({draft.products.length})</CardTitle>
                <Button data-testid="add-product-btn" onClick={addProduct} size="sm" variant="outline" className="rounded-none">
                  <Plus className="h-4 w-4 mr-1" /> Produkt
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                {draft.products.length === 0 && (
                  <p className="text-sm text-gray-500 italic">Noch keine Produkte hinzugefügt</p>
                )}
                {draft.products.map((p, idx) => (
                  <div key={idx} className="border border-[#E8E0D5] p-3 space-y-2 bg-[#FAF7F2]" data-testid={`product-block-${idx}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-[#8B7355]">Produkt {idx + 1}</span>
                      <Button data-testid={`remove-product-${idx}`} onClick={() => removeProduct(idx)} size="sm" variant="ghost" className="text-red-600 h-6 px-2">
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                    <Input placeholder="Titel" value={p.title} onChange={(e) => updateProduct(idx, 'title', e.target.value)} />
                    <Textarea placeholder="Beschreibung" value={p.description} onChange={(e) => updateProduct(idx, 'description', e.target.value)} rows={2} />
                    <div className="grid grid-cols-2 gap-2">
                      <Input placeholder="Preis (z.B. 24,90 €)" value={p.price} onChange={(e) => updateProduct(idx, 'price', e.target.value)} />
                      <Input placeholder="Produkt-Link" value={p.link} onChange={(e) => updateProduct(idx, 'link', e.target.value)} />
                    </div>
                    <Input placeholder="Bild-URL" value={p.image_url} onChange={(e) => updateProduct(idx, 'image_url', e.target.value)} />
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* CTA */}
            <Card className="rounded-none border-[#C4A084]">
              <CardHeader className="py-3"><CardTitle className="text-base">Call-to-Action Button</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Button-Text</Label>
                    <Input value={draft.cta_label} onChange={(e) => update('cta_label', e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-xs">Ziel-Link</Label>
                    <Input value={draft.cta_link} onChange={(e) => update('cta_link', e.target.value)} />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Saved Drafts */}
            {drafts.length > 0 && (
              <Card className="rounded-none border-[#C4A084]">
                <CardHeader className="py-3"><CardTitle className="text-base">Gespeicherte Entwürfe</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {drafts.map((d) => (
                      <button
                        key={d.id}
                        onClick={() => loadDraft(d)}
                        data-testid={`load-draft-${d.id}`}
                        className="w-full text-left p-2 bg-white hover:bg-[#FAF7F2] border border-[#E8E0D5] flex justify-between items-center"
                      >
                        <div>
                          <p className="text-sm font-medium">{d.title}</p>
                          <p className="text-xs text-gray-500">{d.subject}</p>
                        </div>
                        <span className={`text-xs px-2 py-1 ${
                          d.status === 'sent' ? 'bg-green-100 text-green-700' :
                          d.status === 'awaiting_approval' ? 'bg-amber-100 text-amber-700' :
                          d.status === 'waiting' ? 'bg-blue-100 text-blue-700' :
                          d.status === 'rejected' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {d.status === 'sent' ? 'Versendet' :
                           d.status === 'awaiting_approval' ? 'Wartet auf Freigabe' :
                           d.status === 'waiting' ? 'Pausiert' :
                           d.status === 'rejected' ? 'Abgelehnt' : 'Entwurf'}
                        </span>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* RIGHT: PREVIEW */}
          <div className="space-y-4 lg:sticky lg:top-4 lg:self-start">
            <Card className="rounded-none border-[#C4A084]">
              <CardHeader className="py-3 flex flex-row items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Eye className="h-4 w-4" /> Live-Vorschau
                </CardTitle>
                <div className="flex gap-1">
                  <Button
                    data-testid="preview-desktop-btn"
                    size="sm"
                    variant={previewMode === 'desktop' ? 'default' : 'outline'}
                    onClick={() => setPreviewMode('desktop')}
                    className="rounded-none h-8"
                  >
                    <Monitor className="h-3 w-3" />
                  </Button>
                  <Button
                    data-testid="preview-mobile-btn"
                    size="sm"
                    variant={previewMode === 'mobile' ? 'default' : 'outline'}
                    onClick={() => setPreviewMode('mobile')}
                    className="rounded-none h-8"
                  >
                    <Smartphone className="h-3 w-3" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-2 bg-gray-100">
                <div className="mx-auto bg-white" style={{ maxWidth: previewMode === 'mobile' ? 380 : '100%' }}>
                  <iframe
                    title="Newsletter-Vorschau"
                    srcDoc={previewHtml}
                    className="w-full"
                    style={{ height: previewMode === 'mobile' ? 700 : 800, border: 0 }}
                    data-testid="newsletter-preview-iframe"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <Card className="rounded-none border-[#C4A084]">
              <CardContent className="space-y-3 pt-4">
                <div className="flex gap-2">
                  <Button data-testid="save-draft-btn" onClick={handleSave} disabled={saving} className="flex-1 rounded-none bg-[#8B7355] hover:bg-[#6D5A47] text-white">
                    <Save className="h-4 w-4 mr-2" /> {saving ? 'Speichere...' : 'Speichern'}
                  </Button>
                  <Button data-testid="new-draft-btn" onClick={() => setDraft(emptyDraft())} variant="outline" className="rounded-none">
                    Neu
                  </Button>
                </div>

                <div className="border-t border-[#E8E0D5] pt-3">
                  <Label className="text-xs">Test-E-Mail an:</Label>
                  <div className="flex gap-2 mt-1">
                    <Input data-testid="test-email-input" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="E-Mail-Adresse" />
                    <Button data-testid="test-send-btn" onClick={handleTestSend} variant="outline" className="rounded-none">
                      <Send className="h-4 w-4 mr-1" /> Test
                    </Button>
                  </div>
                </div>

                <div className="border-t border-[#E8E0D5] pt-3 space-y-2">
                  {draft.id && draft.status && (
                    <div className="flex items-center justify-center gap-2 pb-2">
                      <span className="text-xs text-gray-500">Status:</span>
                      <span className={`text-xs px-3 py-1 font-medium ${
                        draft.status === 'sent' ? 'bg-green-100 text-green-700' :
                        draft.status === 'awaiting_approval' ? 'bg-amber-100 text-amber-700' :
                        draft.status === 'waiting' ? 'bg-blue-100 text-blue-700' :
                        draft.status === 'rejected' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {draft.status === 'sent' ? '✓ Versendet' :
                         draft.status === 'awaiting_approval' ? '⏳ Wartet auf Freigabe' :
                         draft.status === 'waiting' ? '⏸ Pausiert' :
                         draft.status === 'rejected' ? '✗ Abgelehnt' : '📝 Entwurf'}
                      </span>
                    </div>
                  )}
                  <Button
                    data-testid="request-approval-btn"
                    onClick={() => setShowApprovalDialog(true)}
                    disabled={!draft.id}
                    className="w-full rounded-none bg-amber-500 hover:bg-amber-600 text-white"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" /> Zur Freigabe einreichen
                  </Button>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      data-testid="snooze-btn"
                      onClick={handleSnooze}
                      disabled={!draft.id}
                      variant="outline"
                      className="rounded-none border-blue-400 text-blue-700 hover:bg-blue-50"
                    >
                      ⏸ Warten lassen
                    </Button>
                    <Button
                      data-testid="reject-btn"
                      onClick={handleReject}
                      disabled={!draft.id}
                      variant="outline"
                      className="rounded-none border-red-400 text-red-700 hover:bg-red-50"
                    >
                      ✗ Verwerfen
                    </Button>
                  </div>
                  <Button
                    data-testid="approve-send-btn"
                    onClick={handleApproveAndSend}
                    disabled={!draft.id}
                    className="w-full rounded-none bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Send className="h-4 w-4 mr-2" /> Freigeben & jetzt versenden
                  </Button>
                  {!draft.id && (
                    <p className="text-xs text-gray-500 italic text-center">Bitte erst speichern</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Approval Dialog */}
        {showApprovalDialog && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="approval-dialog">
            <div className="bg-white max-w-md w-full p-6 rounded-none border-2 border-[#C4A084]">
              <h3 className="text-lg font-semibold mb-2">Newsletter zur Freigabe einreichen</h3>
              <p className="text-sm text-gray-600 mb-4">
                Der Entwurf wird auf "Wartet auf Freigabe" gesetzt. Sie erhalten eine Benachrichtigung im Admin-Bereich
                und können dann den Versand bestätigen oder ablehnen.
              </p>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowApprovalDialog(false)} className="rounded-none">Abbrechen</Button>
                <Button onClick={handleRequestApproval} className="rounded-none bg-amber-500 hover:bg-amber-600 text-white">
                  Einreichen
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Build HTML preview (mirrors backend rendering)
function buildPreviewHtml(d) {
  const accent = d.accent_color || '#C4A084';
  const heroImg = d.hero_image ? `<img src="${d.hero_image}" alt="" style="width:100%;display:block;border:0"/>` : '';
  const introHtml = d.intro_text ? `<p style="font-size:16px;line-height:1.6;color:#555;margin:0 0 18px 0;">${escapeHtml(d.intro_text)}</p>` : '';
  const productsHtml = d.products && d.products.length
    ? `<table width="100%" cellpadding="0" cellspacing="0">${d.products.map((p) => {
        const img = p.image_url ? `<img src="${p.image_url}" alt="${escapeHtml(p.title)}" style="width:100%;max-width:520px;display:block;border:0;"/>` : '';
        const price = p.price ? `<p style="color:${accent};font-weight:bold;margin:6px 0;">${escapeHtml(p.price)}</p>` : '';
        const link = p.link ? `<a href="${p.link}" style="display:inline-block;margin-top:8px;color:${accent};text-decoration:underline;font-size:14px;">Mehr ansehen →</a>` : '';
        return `<tr><td style="padding:16px 0;border-bottom:1px solid #eee;">${img}<h3 style="color:#6D5A47;margin:12px 0 4px 0;font-size:18px;">${escapeHtml(p.title || '')}</h3>${price}<p style="color:#666;font-size:14px;line-height:1.5;margin:4px 0;">${escapeHtml(p.description || '')}</p>${link}</td></tr>`;
      }).join('')}</table>`
    : '';
  const footerNote = d.footer_note || 'Du erhältst diese E-Mail, weil du dich für unseren Newsletter angemeldet hast.';

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head>
<body style="margin:0;padding:0;background:#E8E0D5;font-family:Arial,sans-serif;color:#333;">
<div style="max-width:600px;margin:0 auto;background:#fff;">
  <div style="background:linear-gradient(135deg,${accent} 0%,#B89968 100%);padding:30px;text-align:center;">
    <h1 style="color:#fff;margin:0;font-size:28px;letter-spacing:2px;">Homeyoursoul</h1>
    <p style="color:#FFF8E0;margin:6px 0 0 0;font-size:12px;letter-spacing:3px;text-transform:uppercase;">Newsletter</p>
  </div>
  ${heroImg}
  <div style="padding:30px;">
    <h2 style="color:#6D5A47;font-size:22px;margin:0 0 12px 0;">${escapeHtml(d.hero_title || '')}</h2>
    <p style="font-size:16px;line-height:1.6;color:#555;margin:0 0 18px 0;">${escapeHtml(d.hero_text || '')}</p>
    ${introHtml}
    ${productsHtml}
    <div style="text-align:center;margin:30px 0 10px 0;">
      <a href="${d.cta_link || '#'}" style="display:inline-block;background:linear-gradient(135deg,${accent} 0%,#B89968 100%);color:#fff;padding:14px 40px;text-decoration:none;font-size:16px;letter-spacing:1px;">${escapeHtml(d.cta_label || 'Jetzt entdecken')}</a>
    </div>
  </div>
  <div style="background:#6D5A47;color:#FFF8E0;padding:24px;text-align:center;font-size:12px;line-height:1.6;">
    <p style="margin:0 0 8px 0;"><strong>Homeyoursoul</strong> · Yasemin Kurt-Yilmaz · Hechingerstr. 20 · 72406 Bisingen</p>
    <p style="margin:0 0 8px 0;">E-Mail: kontakt@homeyoursoul.de · Kleinunternehmer gem. § 19 UStG</p>
    <p style="margin:8px 0 0 0;">${escapeHtml(footerNote)}<br>
      <a href="#" style="color:#FFF8E0;text-decoration:underline;">Hier abmelden</a> ·
      <a href="https://homeyoursoul.com/impressum" style="color:#FFF8E0;text-decoration:underline;">Impressum</a> ·
      <a href="https://homeyoursoul.com/datenschutz" style="color:#FFF8E0;text-decoration:underline;">Datenschutz</a>
    </p>
  </div>
</div></body></html>`;
}

function escapeHtml(s) {
  if (!s) return '';
  return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
